import { z } from 'zod';
import type { ExerciseKind } from '../domain/exercise';

/**
 * The JSON shapes stored in Core's Stronger Memory content types (docs/mvp/03-CONTENT.md).
 * Core keeps them as opaque JSON; this is the one place the app says what they look like,
 * so a bad row degrades into "skip it" instead of a crash.
 */

/** Core's `kind` values; the app's `ExerciseKind` has longer names. */
export const smKindSchema = z.enum(['read', 'write', 'math']);
export type SmKind = z.infer<typeof smKindSchema>;

export const SM_KIND: Record<ExerciseKind, SmKind> = {
  reading: 'read',
  writing: 'write',
  math: 'math',
};
export const EXERCISE_KIND: Record<SmKind, ExerciseKind> = {
  read: 'reading',
  write: 'writing',
  math: 'math',
};

export const smProblemSchema = z.object({
  a: z.number().int(),
  op: z.enum(['+', '-']),
  b: z.number().int(),
  answer: z.number().int(),
});
export type SmProblem = z.infer<typeof smProblemSchema>;

export const smSheetValueSchema = z.object({
  stage: z.number().int().positive(),
  sheet: z.number().int().positive(),
  page: z.number().int().positive(),
  problems: z.array(smProblemSchema).min(1),
  brainFact: z.string().default(''),
  locale: z.string().default('en-US'),
});

/** SM Reading Passage — a passage to read aloud. */
export const smPassageValueSchema = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  sequence: z.number().int().positive(),
  locale: z.string().default('en-US'),
  source: z.string().optional(),
});
export type SmPassageValue = z.infer<typeof smPassageValueSchema>;

/** SM Library — the read-aloud library's container: its categories, in order. */
export const smLibraryValueSchema = z.object({
  key: z.string().min(1),
  title: z.string().optional(),
  categories: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })).catch([]),
});

/**
 * SM Reading Passage as the library writes it (`schema: 2`), with no body: the text is its own
 * child node, read when the passage is opened. Only key and title are required, so a passage
 * missing something else still shows.
 */
export const smLibraryPassageSchema = z.object({
  schema: z.literal(2),
  key: z.string().min(1),
  title: z.string().min(1),
  author: z.string().optional().catch(undefined),
  kind: z.string().optional().catch(undefined),
  label: z.string().optional().catch(undefined),
  categories: z.array(z.string()).catch([]),
  summary: z.string().optional().catch(undefined),
  /** Estimated reading time, in minutes, at 180 words a minute. */
  minutes: z.object({ at180wpm: z.number().positive() }).optional().catch(undefined),
  cover: z
    .object({
      background: z.string().optional(),
      foryou: z.string().optional(),
      savedtall: z.string().optional(),
    })
    .optional()
    .catch(undefined),
  retired: z.boolean().optional().catch(undefined),
});
export type SmLibraryPassage = z.infer<typeof smLibraryPassageSchema>;

/** SM Passage Text — the text of one library passage. */
export const smPassageTextSchema = z.object({ body: z.string().min(1) });

export const smPromptValueSchema = z.object({
  number: z.number().int().positive(),
  text: z.string().min(1),
  locale: z.string().default('en-US'),
});

export const smSessionValueSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timeZone: z.string().optional(),
  groupActorUuid: z.string().optional(),
});

/** One answer to a workbook sum, `a op b`. */
const smSumAnswerSchema = z.object({
  index: z.number().int().nonnegative(),
  a: z.number().int(),
  op: z.enum(['+', '-']),
  b: z.number().int(),
  options: z.array(z.number().int()),
  chosen: z.number().int(),
  correct: z.number().int(),
  isCorrect: z.boolean(),
  responseMs: z.number().int().nonnegative().optional(),
});

/**
 * One answer to a Math library problem: the problem as the library has it, and for a
 * countdown which of its steps (`step`). A clock's choices are times ("12:34").
 */
const smLibraryAnswerSchema = z.object({
  index: z.number().int().nonnegative(),
  problem: z.lazy(() => mathProblemSchema),
  step: z.number().int().nonnegative().optional(),
  options: z.array(z.union([z.number().int(), z.string()])),
  chosen: z.union([z.number().int(), z.string()]),
  correct: z.union([z.number().int(), z.string()]),
  isCorrect: z.boolean(),
  responseMs: z.number().int().nonnegative().optional(),
});

export const smAnswerValueSchema = z.union([smSumAnswerSchema, smLibraryAnswerSchema]);
export type SmAnswer = z.infer<typeof smAnswerValueSchema>;

const count = z.number().int();
const time = z.string().regex(/^\d{1,2}:\d{2}$/);

/**
 * A problem of the Math library (scripts/seeds/quick-math), with its answer worked out by the
 * seed. A kind the app doesn't know fails to parse and is left out of its sheet.
 */
export const mathProblemSchema = z.discriminatedUnion('kind', [
  /** Vertical arithmetic: `op` applies down the column (30 − 20 − 10). */
  z.object({
    kind: z.literal('column'),
    op: z.enum(['+', '-']),
    operands: z.array(count).min(2),
    answer: count,
  }),
  z.object({ kind: z.literal('dice'), dice: z.array(count.min(1).max(6)).min(1), answer: count }),
  /** `a op __ = total`. */
  z.object({
    kind: z.literal('missing'),
    a: count,
    op: z.enum(['+', '-']),
    total: count,
    answer: count,
  }),
  z.object({
    kind: z.literal('symbols'),
    clues: z.array(z.string().min(1)).min(1),
    question: z.string().min(1),
    /** Each symbol's value, which every clue gives one of. */
    values: z.record(z.string(), count),
    answer: count,
  }),
  /** The answer is the next term. */
  z.object({ kind: z.literal('sequence'), terms: z.array(count).min(2), answer: count }),
  /** Subtract `step` from `start`, then keep going: one answer per step. */
  z.object({
    kind: z.literal('countdown'),
    start: count,
    step: count.positive(),
    answers: z.array(count).min(1),
  }),
  /** The time `minutes` after `start` (before it, when negative). */
  z.object({ kind: z.literal('clock'), start: time, minutes: count, answer: time }),
]);
export type MathProblem = z.infer<typeof mathProblemSchema>;

/** SM Math Set — one row of the Math library: a variation and its practice sheets. */
export const smMathSetSchema = z.object({
  schema: z.literal(2),
  key: z.string().min(1),
  title: z.string().min(1),
  category: z.string().min(1),
  levels: z.array(z.string()).catch([]),
  format: z.string().optional().catch(undefined),
  summary: z.string().optional().catch(undefined),
  example: z.string().optional().catch(undefined),
  icon: z.string().optional().catch(undefined),
  instruction: z.string().optional().catch(undefined),
  retired: z.boolean().optional().catch(undefined),
});

/** SM Math Sheet as the Math library writes it (`schema: 2`): one practice sheet of a row. */
export const smMathPracticeSheetSchema = z.object({
  schema: z.literal(2),
  key: z.string().min(1),
  sheet: z.number().int().positive(),
  problems: z.array(z.unknown()),
});

export const smAttemptValueSchema = z.object({
  kind: smKindSchema,
  state: z.enum(['open', 'done']).default('done'),
  source: z.enum(['workbook', 'own', 'other']).default('own'),
  sourceTitle: z.string().optional(),
  startedAt: z.string(),
  completedAt: z.string().optional(),
  activeSeconds: z.number().int().nonnegative().default(0),
  /** `self` when the participant did it; a Group Leader marking for a Member sets `leader`. */
  markedBy: z.enum(['self', 'leader']).default('self'),
  groupActorUuid: z.string().optional(),
});
export type SmAttemptValue = z.infer<typeof smAttemptValueSchema>;

/** Sessions a member is expected to complete in a week — the "of 5" on the Home leaderboard. */
export const WEEK_TARGET = 5;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * The one participant object shared with the group (Home › Group progress). Facilitator-safe
 * by construction (PRD-1945, "Scope limit on Facilitator visibility"): what was completed and
 * when, never how long it took, the answers, or the accuracy — those stay in the sessions,
 * which only their owner can read.
 */
export const smSummaryValueSchema = z.object({
  asOf: isoDate,
  currentStreak: z.number().int().nonnegative(),
  bestStreak: z.number().int().nonnegative(),
  sessionsThisMonth: z.number().int().nonnegative(),
  /** Days the whole routine was ever completed — achievements ("30 sessions"). */
  sessionsTotal: z.number().int().nonnegative().optional(),
  completionByKind: z.record(smKindSchema, z.number().int().nonnegative()),
  /** Monday of the week `weekDaysDone` counts, ISO date. */
  weekStart: isoDate,
  weekTarget: z.number().int().positive(),
  /** Days of that week on which the whole routine was done, marks included. */
  weekDaysDone: z.array(isoDate),
  /**
   * For each day of that week, the exercises the member finished themselves: marks left out,
   * and days with none too. echo's backend adds the marks that are done now (`memberWeek`), so
   * an undone mark stops counting at once. Absent from summaries written before marks.
   */
  weekDoneBySelf: z.record(isoDate, z.array(smKindSchema)).optional(),
  groupActorUuid: z.string().optional(),
});
export type SmSummary = z.infer<typeof smSummaryValueSchema>;

/**
 * A leader's "marked done" (specs/v1-2-0/01-group-progress-and-marks.md, section 3): an
 * `SM Exercise Attempt` that echo's backend writes as the Stronger Memory group, one lineage per
 * sub-group, member, day and exercise, never inside a session. Undoing it is a new version with
 * `state: 'undone'`. Written with the attempt's own fields as well (`markValue`), so a done mark
 * also reads as an attempt with `markedBy: 'leader'`, and an undone one doesn't.
 */
export const smMarkValueSchema = z.object({
  kind: smKindSchema,
  state: z.enum(['done', 'undone']),
  markedBy: z.literal('leader'),
  /** The member's day the exercise counts for. */
  date: isoDate,
  memberActorUuid: z.string().min(1),
  /** The sub-group the leader marked it in; only that group's leaders may undo it. */
  groupActorUuid: z.string().min(1),
  /** Who set the current state: marked it, or undid it. */
  leaderActorUuid: z.string().min(1),
  /** When the current state was set, ISO time. */
  changedAt: z.string().min(1),
});
export type SmMarkValue = z.infer<typeof smMarkValueSchema>;
